import React, { useRef, useCallback } from 'react';
import classnames from 'classnames';

import Check from '@/shared/assets/Check';
import { Menu } from '@/shared/components/Menu/Menu';
import { MenuItem } from '@/shared/components/MenuItem/MenuItem';
import {
    Dropdown,
    type DropdownRef,
    type RenderDropdownTrigger,
} from '@/shared/components/Dropdown/Dropdown';

import styles from './SettingsDropdown.module.css';

type DefaultOption = { title: string };

type Props<T> = {
    defaultDropdownButtonTitle: string;
    selectedOptionTitle?: string;
    options: T[];
    onChangeOption: (option: T) => void;
    onDropdownButtonOpen?: () => void;
    onSelectOption?: (option: T) => void;
};

export const SettingsDropdown: <T extends DefaultOption>(props: Props<T>) => React.ReactElement = ({
    defaultDropdownButtonTitle,
    selectedOptionTitle,
    options,
    onChangeOption,
    onDropdownButtonOpen,
    onSelectOption,
}) => {
    const dropdownRef = useRef<DropdownRef>(null);

    const renderDropdownTrigger: RenderDropdownTrigger<HTMLDivElement> = useCallback(
        ({ onClick, ref, isActive }) => (
            <div
                ref={ref}
                className={classnames(styles.dropdownButton, {
                    [styles.dropdownButtonActive]: isActive,
                })}
                onClick={() => {
                    if (!isActive) {
                        onDropdownButtonOpen?.();
                    }
                    onClick();
                }}
                role="presentation"
            >
                <span>{selectedOptionTitle || defaultDropdownButtonTitle}</span>
                <span className={classnames(styles.dropdownIcon)} />
            </div>
        ),
        [onDropdownButtonOpen, selectedOptionTitle, defaultDropdownButtonTitle]
    );

    return (
        <Dropdown
            className={styles.dropdown}
            contentClassName={styles.dropdownContent}
            ref={dropdownRef}
            renderDropdownTrigger={renderDropdownTrigger}
            content={
                <Menu className={styles.dropdownMenu}>
                    {options.map((option) => (
                        <MenuItem
                            key={option.title}
                            text={option.title}
                            onClick={() => {
                                dropdownRef?.current?.close();

                                onSelectOption?.(option);

                                if (option.title !== selectedOptionTitle) {
                                    onChangeOption(option);
                                }
                            }}
                            icon={
                                option.title === selectedOptionTitle ? (
                                    <Check className={styles.doneIcon} />
                                ) : undefined
                            }
                        />
                    ))}
                </Menu>
            }
        />
    );
};
